import { useEffect } from "react";

interface PageMetaProps {
  title: string;
  description: string;
  /** Canonical path, e.g. "/coloring-shop" */
  path?: string;
  /** Optional OG image override, defaults to site og-image */
  image?: string;
}

const SITE = "https://gofluffandstuff.com";
const DEFAULT_IMAGE = `${SITE}/og-image.jpg`;

/**
 * Imperatively updates <title> and key <meta> tags for SEO.
 * No external dependency needed — React's useEffect handles cleanup.
 */
export function PageMeta({ title, description, path = "/", image = DEFAULT_IMAGE }: PageMetaProps) {
  useEffect(() => {
    const fullTitle = title.includes("Go Fluff") ? title : `${title} | Go Fluff and Stuff`;
    const canonicalUrl = `${SITE}${path}`;

    // <title>
    document.title = fullTitle;

    // Helpers
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        const [attrName, attrVal] = selector.match(/\[(.+?)="(.+?)"\]/)?.slice(1) ?? [];
        if (attrName) el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    return () => {
      document.title = "Go Fluff and Stuff";
    };
  }, [title, description, path, image]);

  return null;
}
